function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

export const MAX_INDENT_DEPTH = 6;
export const INITIAL_VISIBLE_CHILDREN = 3;
export const INITIAL_VISIBLE_BRANCH_NODES = 3;
export const MAX_INLINE_THREAD_DEPTH = 5;

/**
 * Parents at or beyond this depth show all direct replies at once (no grey "+N more replies").
 * Keeps sibling pagination from appearing alongside green "continue thread" on the next level
 * (see CommentNode: shouldGateDeepInline uses MAX_INLINE_THREAD_DEPTH - 1).
 */
export const MAX_PARENT_DEPTH_FOR_SIBLING_REPLY_PAGINATION = MAX_INLINE_THREAD_DEPTH - 2;

export const clubThreadSectionClass =
  "rounded-2xl border border-white/4 bg-transparent backdrop-blur-[1px] shadow-[0_4px_16px_rgba(0,0,0,0.12)] scroll-mt-[7.5rem] lg:scroll-mt-28 w-full min-w-0 max-w-full box-border";

export const clubThreadSectionHeaderClass =
  "px-3 sm:px-5 md:px-6 pt-3.5 sm:pt-4 pb-3 border-b border-white/5 bg-transparent w-full min-w-0 max-w-full box-border";

export const clubThreadComposerWrapClass =
  "px-3 sm:px-5 md:px-6 pt-4 pb-3.5 w-full min-w-0 max-w-full box-border";

export const clubThreadCommentsWrapClass =
  "px-3 sm:px-5 md:px-6 pt-4 sm:pt-5 pb-5 sm:pb-6 min-h-[140px] w-full min-w-0 max-w-full box-border overflow-visible";

export const clubThreadToolsRowClass =
  "px-3 sm:px-5 md:px-6 py-3 border-b border-white/4 w-full min-w-0 max-w-full box-border flex items-center justify-between gap-3";

export const clubThreadSortTriggerClass =
  "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-white/10 bg-white/[0.02] text-[11px] sm:text-xs font-medium text-gray-200 hover:bg-white/[0.06] transition-colors";

export const clubThreadSortMenuClass =
  "absolute left-0 top-full mt-2 z-50 min-w-[148px] rounded-xl border border-white/[0.12] bg-[#0b1018] p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.06]";

export function clubThreadSortMenuItemClass(active = false) {
  if (active) {
    return "w-full text-left inline-flex items-center rounded-lg px-2.5 py-2 text-[11px] sm:text-xs font-semibold text-gray-100 bg-white/[0.09]";
  }
  return "w-full text-left inline-flex items-center rounded-lg px-2.5 py-2 text-[11px] sm:text-xs font-medium text-gray-300 hover:bg-white/[0.06] transition-colors";
}

export const clubThreadSearchBtnClass =
  "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-white/8 bg-transparent text-[11px] sm:text-xs font-medium text-gray-400 hover:text-gray-200 hover:bg-white/[0.05] transition-colors";

export const clubThreadCommentsStackClass =
  "space-y-3 sm:space-y-3.5 overflow-visible";

export const clubThreadCommentItemClass =
  "rounded-xl border border-white/[0.02] bg-white/[0.006] backdrop-blur-[0.5px] shadow-none px-3.5 sm:px-4 py-3 sm:py-3.5 hover:border-white/[0.045] hover:bg-white/[0.015] transition-colors";

/** Minimized thread line (Reddit-style collapsed comment + subtree). */
export const clubThreadCommentCompactItemClass =
  "rounded-xl border border-white/[0.06] bg-white/[0.04] px-2.5 sm:px-3 py-2 hover:border-white/[0.1] hover:bg-white/[0.06] transition-colors";

export const clubThreadCommentCompactAvatarClass =
  "h-7 w-7 rounded-lg border border-white/8 bg-gradient-to-br from-white/[0.05] to-black/35 overflow-hidden shrink-0 grid place-items-center text-[10px] font-semibold text-gray-300 ring-1 ring-white/[0.04]";

/** Same circle as the guide-rail toggle, for in-flow use (e.g. leaf comment collapse). */
export const clubThreadCommentInlineCollapseToggleClass =
  "shrink-0 h-[18px] w-[18px] rounded-full border border-white/16 bg-[#0b121d] text-[12px] leading-none text-gray-200 hover:border-white/26 hover:bg-[#101a29] transition-colors inline-flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.35)]";

export const clubThreadCommentAvatarClass =
  "h-10 w-10 rounded-xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-black/35 overflow-hidden shrink-0 grid place-items-center text-xs font-semibold text-gray-300 ring-1 ring-white/[0.04]";

export const clubThreadCommentBodyClass =
  "mt-2.5 text-sm text-gray-200/92 whitespace-pre-line leading-relaxed break-words";

export const clubThreadCommentMetaNameClass = "text-sm font-semibold text-white";

export const clubThreadCommentMetaTimeClass = "text-xs text-gray-500 tabular-nums";

export const clubThreadChildrenWrapClass = "mt-2 sm:mt-2.5 space-y-2 sm:space-y-2.5";
export const clubThreadCommentActionsRowClass =
  "mt-2.5 flex items-center gap-1.5 sm:gap-2 flex-wrap";

export function clubThreadCommentActionButtonClass({ active = false, disabled = false } = {}) {
  if (disabled) {
    return "shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-transparent bg-transparent text-[11px] sm:text-xs font-medium text-white/40 opacity-70 cursor-not-allowed";
  }
  if (active) {
    return "shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-transparent bg-transparent text-[11px] sm:text-xs font-semibold text-white/60 hover:bg-white/[0.04] hover:text-white/85 focus-visible:bg-white/[0.04] focus-visible:text-white/85 transition";
  }
  return "shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-transparent bg-transparent text-[11px] sm:text-xs font-medium text-white/60 hover:bg-white/[0.04] hover:text-white/85 focus-visible:bg-white/[0.04] focus-visible:text-white/85 transition";
}

export const clubThreadCommentActionIconClass = "h-3.5 w-3.5 shrink-0";
export const clubThreadCommentActionCountClass = "tabular-nums text-xs opacity-95";
export const clubThreadSharePopoverWrapClass = "relative inline-flex";
export const clubThreadSharePopoverClass =
  "absolute left-0 top-[calc(100%+8px)] z-[60] min-w-[176px] rounded-xl border border-white/[0.12] bg-[#080c14]/98 backdrop-blur-xl p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.06]";

/** Fixed menu shell for portals (⋯ comment options) — escapes overflow/stacking from GIFs, images, nested replies. */
export const clubThreadCommentOwnerMenuPortalClass =
  "fixed z-[10040] min-w-[176px] rounded-xl border border-white/[0.12] bg-[#080c14]/98 backdrop-blur-xl p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.06]";

/** Opaque shell for GIF picker — same base as share/options menus (#080c14), no translucency. */
export const clubThreadGifPickerPanelClass = cn(
  "rounded-2xl border border-white/[0.12] bg-[#080c14] p-3",
  "shadow-[0_20px_56px_rgba(0,0,0,0.72)] ring-1 ring-white/[0.06]"
);

/**
 * Inset wells on GIF picker (#050810 matches focus ring offsets elsewhere in club thread UI).
 * Use for search field, close control, and grid tiles so everything stays one dark system.
 */
export const clubThreadGifPickerInsetClass =
  "border border-white/10 bg-[#050810] text-gray-100";
export const clubThreadSharePopoverItemClass =
  "w-full text-left inline-flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium text-white/85 hover:bg-white/[0.08] transition-colors";
export const clubThreadSharePopoverItemMutedClass =
  "w-full text-left inline-flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium text-gray-400 hover:bg-white/[0.05] transition-colors";

export function clubThreadReplyButtonClass({ canOpenReply, isActiveReply }) {
  if (!canOpenReply) {
    return "shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-transparent bg-transparent text-[11px] sm:text-xs font-medium text-white/40 opacity-70 cursor-not-allowed";
  }
  if (isActiveReply) {
    return "shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-transparent bg-transparent text-[11px] sm:text-xs font-semibold text-emerald-300 hover:bg-emerald-400/[0.06] focus-visible:bg-emerald-400/[0.06] transition";
  }
  return "shrink-0 inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-transparent bg-transparent text-[11px] sm:text-xs font-medium text-white/60 hover:bg-white/[0.04] hover:text-white/85 focus-visible:bg-white/[0.04] focus-visible:text-white/85 transition";
}

export const clubThreadInlineComposerWrapClass =
  "mt-2.5 ml-10 sm:ml-12 pl-2.5 sm:pl-3.5 border-l border-white/[0.09]";

export const clubThreadShowMoreButtonClass =
  "inline-flex items-center rounded-md border border-white/8 bg-transparent px-2 py-0.5 text-[11px] sm:text-xs font-medium text-gray-300/90 hover:text-gray-100 hover:border-white/14 hover:bg-white/[0.03] transition-colors";

export const clubThreadContinueThreadButtonClass =
  "inline-flex items-center rounded-lg border border-emerald-300/25 bg-emerald-500/[0.08] px-2.5 py-1 text-[11px] sm:text-xs font-medium text-emerald-100/95 hover:bg-emerald-500/[0.14] transition-colors";

export function clubThreadIndentClass(depth = 0) {
  const d = Math.min(MAX_INDENT_DEPTH, Math.max(0, Number(depth) || 0));
  if (d <= 0) return "";
  if (d === 1) return "pl-3 sm:pl-4";
  if (d === 2) return "pl-4 sm:pl-6";
  if (d === 3) return "pl-5 sm:pl-8";
  if (d === 4) return "pl-5 sm:pl-9";
  if (d === 5) return "pl-5 sm:pl-10";
  return "pl-5 sm:pl-11";
}

export function clubThreadGuideRailClass(depth = 0) {
  if (Math.min(MAX_INDENT_DEPTH, Number(depth) || 0) <= 0) return "";
  return "border-l border-white/[0.11]";
}

export const clubThreadGuideElbowClass =
  "pointer-events-none absolute -left-px top-[2px] h-3.5 w-3.5 border-l border-b border-white/[0.13] rounded-bl-md transition-colors duration-150 group-hover/thread-segment:border-white/[0.24]";

export const clubThreadChildBranchClass =
  "relative ml-2.5 sm:ml-3.5 pl-2.5 sm:pl-3.5 space-y-2 sm:space-y-2.5";

export function clubThreadChildRailClass(active = false) {
  return [
    "pointer-events-none absolute left-0 top-0 bottom-0 border-l transition-colors duration-150",
    active ? "border-white/[0.23]" : "border-white/[0.11]",
  ].join(" ");
}

export function clubThreadChildElbowClass(active = false) {
  return [
    "pointer-events-none absolute left-0 top-3.5 h-3.5 w-3.5 border-l border-b rounded-bl-md transition-colors duration-150",
    active ? "border-white/[0.24]" : "border-white/[0.13]",
  ].join(" ");
}

export const clubThreadChildRailHoverTargetClass =
  "absolute left-0 top-0 bottom-0 w-4 z-[1]";

export const clubThreadRailToggleButtonClass =
  "absolute z-[2] -left-[10px] top-1 h-[18px] w-[18px] rounded-full border border-white/16 bg-[#0b121d] text-[12px] leading-none text-gray-200 hover:border-white/26 hover:bg-[#101a29] transition-colors inline-flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.35)]";

export function clubThreadCommentStateHintClass(tone = "muted") {
  if (tone === "muted") return "text-[11px] sm:text-xs text-gray-500";
  if (tone === "accent") return "text-[11px] sm:text-xs text-emerald-300/90";
  return cn("text-[11px] sm:text-xs", String(tone || ""));
}

export const clubThreadSortWrapClass =
  "inline-flex items-center gap-1 rounded-xl border border-white/8 bg-white/[0.02] p-1";

export function clubThreadSortBtnClass(active = false) {
  if (active) {
    return "inline-flex items-center h-7 px-2.5 rounded-lg bg-white/[0.12] text-[11px] sm:text-xs font-semibold text-gray-100";
  }
  return "inline-flex items-center h-7 px-2.5 rounded-lg text-[11px] sm:text-xs font-medium text-gray-400 hover:text-gray-200 hover:bg-white/[0.05] transition-colors";
}
